import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, notification, Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import {
  useGetTaskCategoryPricesQuery,
  useUpdateTaskCategoryPriceMutation,
} from 'app/api';
import TaskCategorySelect from 'app/components/Select/TaskCategorySelect/TaskCategorySelect';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { useGlobalSlice } from 'app/containers/Global/slice';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toFixedIfFloat } from 'utils/helpers';
import {
  ITaskCategoryPrice,
  IUpdateTaskCategoryPricePayload,
} from '../../types';
import TaskCategoryPriceDrawer from '../TaskCategoryPriceDrawer/TaskCategoryPriceDrawer';

interface IProps {}

const TaskCategoriesSettingsCard: FC<IProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [selectedTableItems, setSelectedTableItems] = useState<
    ITaskCategoryPrice[]
  >([]);

  const [isPriceDrawerOpen, setIsPriceDrawerOpen] = useState(false);

  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);
  const companyId = useSelector(selectSelectedCompanyId);

  const [selectedTaskCategoryId, setSelectedTaskCategoryId] = useState<
    number | undefined
  >();

  const {
    data: taskCategoryPrices,
    isFetching,
  } = useGetTaskCategoryPricesQuery(
    {
      envId: envId as number,
      seasonId: seasonId as number,
      companyId,
      taskCategoryId: selectedTaskCategoryId,
    },
    { skip: !envId || !seasonId },
  );

  const [
    updateTaskCategoryPrice,
    { isLoading },
  ] = useUpdateTaskCategoryPriceMutation();

  const getRowKey = (taskCategoryPrice: ITaskCategoryPrice) =>
    taskCategoryPrice.companyId + taskCategoryPrice.taskCategory.id;

  const columns: ColumnsType<ITaskCategoryPrice> = [
    {
      title: t('TaskCategoriesCard.Table.TaskCategory', 'Task category'),
      dataIndex: ['taskCategory', 'name'],
    },
    {
      title: t('TaskCategoriesCard.Table.Company', 'Company'),
      dataIndex: 'companyName',
    },
    {
      title: t('TaskCategoriesCard.Table.PrimeCost', 'Prime cost (Eur)'),
      dataIndex: ['taskCategoryPrice', 'price'],
      render: (price?: number) => (price ? toFixedIfFloat(price) : '-'),
    },
    {
      title: t('TaskCategoriesCard.Table.SellingPrice', 'Selling Price (Eur)'),
      dataIndex: ['taskCategoryPrice', 'sellingPrice'],
      render: (sellingPrice?: number) =>
        sellingPrice ? toFixedIfFloat(sellingPrice) : '-',
    },
    {
      title: t('TaskCategoriesCard.Table.Efficiency', 'Efficiency (Ha/h)'),
      dataIndex: ['taskCategoryPrice', 'efficiency'],
      render: (efficiency?: number) =>
        efficiency ? toFixedIfFloat(efficiency) : '-',
    },
  ];

  useEffect(() => {
    setSelectedTableItems([]);
  }, [taskCategoryPrices]);

  const rowSelection: TableRowSelection<ITaskCategoryPrice> = {
    type: 'checkbox',
    selectedRowKeys: selectedTableItems.map(getRowKey),
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedTableItems(selectedRows);
    },
  };

  const onPriceChangeSubmit = async ({
    price,
    sellingPrice,
    efficiency,
  }: {
    price: number;
    sellingPrice: number;
    efficiency: number;
  }) => {
    try {
      if (!envId || !seasonId) {
        return;
      }

      const payloadsArr = selectedTableItems.map(taskCategoryPrice => {
        const payload: IUpdateTaskCategoryPricePayload = {
          companyId: taskCategoryPrice.companyId,
          efficiency,
          hiikoEnvironmentId: envId,
          price,
          seasonId,
          sellingPrice,
          taskCategoryId: taskCategoryPrice.taskCategory.id,
          taskCategoryPriceId:
            taskCategoryPrice.taskCategoryPrice?.taskCategoryPriceId || null,
        };
        return payload;
      });

      await Promise.all(
        payloadsArr.map(payload => updateTaskCategoryPrice(payload).unwrap()),
      );
      setSelectedTableItems([]);
      setIsPriceDrawerOpen(false);

      notification.success({
        message: t(
          'TaskCategoriesCard.PriceChangeSuccess',
          'Prices updated successfully',
        ),
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  return (
    <Card
      bordered={false}
      title={t('TaskCategoriesCard.Title', 'Task categories')}
      style={{
        maxHeight: 'calc(100vh - 163px)',
        overflow: 'auto',
        marginLeft: '12px',
      }}
    >
      <Row
        style={{ margin: '0 0 13px 0' }}
        justify="space-between"
        align="middle"
      >
        <TaskCategorySelect
          bordered={true}
          value={selectedTaskCategoryId}
          dropdownMatchSelectWidth={false}
          onChange={value => setSelectedTaskCategoryId(value)}
          allowClear={true}
        />
        <Dropdown
          trigger={['click']}
          disabled={!selectedTableItems.length}
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => {
                  setIsPriceDrawerOpen(true);
                }}
              >
                {t('TaskCategoriesCard.ChangeParameters', 'Change parameters')}
              </Menu.Item>
            </Menu>
          }
        >
          <Button
            type="primary"
            shape="circle"
            icon={<EllipsisOutlined />}
            size="small"
          />
        </Dropdown>
      </Row>

      <StyledTable
        rowKey={getRowKey}
        dataSource={taskCategoryPrices}
        columns={columns}
        rowSelection={rowSelection}
        loading={isFetching}
      />

      <TaskCategoryPriceDrawer
        isOpen={isPriceDrawerOpen}
        onClose={() => setIsPriceDrawerOpen(false)}
        onSubmit={onPriceChangeSubmit}
        isSubmitLoading={isLoading}
      />
    </Card>
  );
};

export default TaskCategoriesSettingsCard;
